import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Loader2, User, Building2, Globe2, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/lib/seo";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { PAYOUT_REGIONS } from "@/lib/mockData";

const TextField = ({ id, label, type = "text", value, onChange, placeholder, icon: Icon, autoComplete }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-ink">
      {label}
    </label>
    <div className="relative mt-1.5">
      <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slatey" aria-hidden="true" />
      <input
        id={id}
        name={id}
        type={type}
        required
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        data-testid={`signup-${id}`}
        className="brand-ring w-full rounded-xl border border-stroke bg-white py-3 pl-10 pr-4 text-sm text-ink placeholder:text-slate-400 transition-shadow"
      />
    </div>
  </div>
);

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    org: "",
    market: PAYOUT_REGIONS[0],
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await signup(form);
    setLoading(false);
    setDone(true);
    toast.success("Business account created — redirecting to your dashboard");
    setTimeout(() => navigate("/dashboard"), 1300);
  };

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <Seo
        title="Create Business Account"
        description="Create your Elikya Technology business account and start accepting payments in minutes."
        path="/signup"
        robots="noindex, follow"
      />
      <header className="px-5 py-5 sm:px-8">
        <Logo />
      </header>

      <main className="flex flex-1 items-center justify-center px-5 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-lg"
        >
          <div className="rounded-2xl border border-stroke bg-white p-8 shadow-float">
            {done ? (
              <div data-testid="signup-success" className="py-6 text-center">
                <motion.div
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 220, damping: 16 }}
                  className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600"
                >
                  <Check className="h-7 w-7" />
                </motion.div>
                <h1 className="mt-5 text-2xl font-bold tracking-tight text-ink">Your account is ready</h1>
                <p className="mt-2 text-sm text-slatey">
                  We're setting up payments for <span className="font-semibold text-ink">{form.org || "your business"}</span>.
                  Taking you to your dashboard…
                </p>
                <Loader2 className="mx-auto mt-5 h-5 w-5 animate-spin text-brand" />
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold tracking-tight text-ink">Create your business account</h1>
                <p className="mt-1.5 text-sm text-slatey">Free to start. No credit card. Accept payments in minutes.</p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                  <TextField id="fullName" label="Full Name" value={form.fullName} onChange={set("fullName")} placeholder="Amara Okonkwo" icon={User} autoComplete="name" />
                  <TextField id="email" label="Work Email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" icon={Mail} autoComplete="email" />
                  <TextField id="org" label="Business Name" value={form.org} onChange={set("org")} placeholder="Acme Trading Ltd" icon={Building2} autoComplete="organization" />

                  <div>
                    <label htmlFor="market" className="block text-sm font-medium text-ink">
                      Primary Payout Region
                    </label>
                    <div className="relative mt-1.5">
                      <Globe2 className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slatey" aria-hidden="true" />
                      <select
                        id="market"
                        name="market"
                        value={form.market}
                        onChange={set("market")}
                        data-testid="signup-market"
                        className="brand-ring w-full appearance-none rounded-xl border border-stroke bg-white py-3 pl-10 pr-10 text-sm text-ink transition-shadow"
                      >
                        {PAYOUT_REGIONS.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <svg className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slatey" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>

                  <TextField id="password" label="Password" type="password" value={form.password} onChange={set("password")} placeholder="Create a strong password" icon={Lock} autoComplete="new-password" />

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="signup-submit"
                    className="group flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-hover disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Creating account…
                      </>
                    ) : (
                      <>
                        Create Business Account <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

          {!done && (
            <p className="mt-6 text-center text-sm text-slatey">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-brand hover:text-brand-hover">
                Sign in
              </Link>
            </p>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default Signup;
